export interface ApiClientOpts {
  baseUrl: string;
  fetch?: typeof fetch;
}

export interface GetOpts {
  params?: Record<string, string | undefined>;
  signal?: AbortSignal;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(opts: {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    cause?: unknown;
  }) {
    super(opts.message, opts.cause === undefined ? undefined : { cause: opts.cause });
    this.name = 'ApiError';
    this.status = opts.status;
    this.code = opts.code;
    this.details = opts.details;
  }
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetch: typeof fetch;

  constructor(opts: ApiClientOpts) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.fetch = opts.fetch ?? globalThis.fetch.bind(globalThis);
  }

  async get<T>(path: string, opts: GetOpts = {}): Promise<T> {
    const url = this.buildUrl(path, opts.params);
    const init: RequestInit = { credentials: 'include' };
    if (opts.signal) init.signal = opts.signal;
    return this.request<T>(url, init);
  }

  async post<T>(path: string, body: unknown): Promise<T> {
    const url = this.buildUrl(path);
    return this.request<T>(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(this.buildUrl(path), {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(this.buildUrl(path), {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
  }

  async del<T>(path: string): Promise<T> {
    return this.request<T>(this.buildUrl(path), { method: 'DELETE', credentials: 'include' });
  }

  private buildUrl(path: string, params?: Record<string, string | undefined>): string {
    const url = `${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
    if (!params) return url;
    const search = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) search.set(k, v);
    }
    const q = search.toString();
    return q ? `${url}?${q}` : url;
  }

  private async request<T>(url: string, init: RequestInit): Promise<T> {
    let res: Response;
    try {
      res = await this.fetch(url, init);
    } catch (cause) {
      throw new ApiError({
        status: 0,
        code: 'NETWORK',
        message: cause instanceof Error ? cause.message : 'network error',
        cause,
      });
    }
    const contentType = res.headers.get('content-type') ?? '';
    const body = contentType.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
      const err =
        typeof body === 'object' && body !== null
          ? (body as { error?: string; code?: string; details?: unknown })
          : { error: String(body), code: 'HTTP_ERROR' };
      throw new ApiError({
        status: res.status,
        code: err.code ?? 'HTTP_ERROR',
        message: err.error ?? `HTTP ${res.status}`,
        ...(err.details !== undefined ? { details: err.details } : {}),
      });
    }
    return body as T;
  }
}
